import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { toast } from '../../common/Toast/toast';
import { getMe } from "../../../../services/AuthService";
import { getStudentOptions } from "../../../../services/StudentService";
import { getCertificate } from "../../../../services/CertificateService";
import certificate_seven_mentor from "../../../../images/certificate_seven_mentor.webp";
import {
  Award,
  Calendar,
  User,
  Mail,
  Phone,
  AlertCircle,
  Users,
  RefreshCw,
  BookOpen,
} from "lucide-react";
import CustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown";
import { Skeleton, SkeletonGroup } from "../../common/Skeleton/Skeleton.jsx";
import { silenceFor } from "../../common/Loader/busy";
import debounce from "lodash/debounce";

import { isStaffViewer } from "../Dashboard/StudentDashboard2.jsx";
import "../../Trainer/TrainerDashboard.css";
import "./Certificate.css";

/* =============================================================
   CERTIFICATES (/certificate)

   Two views over one fetch, the same split as the dashboard and
   the profile: staff read a list, a student sees the certificates.

   The card image is the SevenMentor certificate TEMPLATE, the same
   picture on every card. The API returns which courses a student
   completed, not a rendered document, so there is nothing
   student-specific to show. The card labels the image "Template"
   rather than letting it read as the student's own certificate,
   and there is no download button because there is no endpoint
   behind one.
   ============================================================= */

const initialsOf = (text, fallback = "?") => {
  const parts = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return parts.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
};

const Certificate = () => {
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const initialized = useRef(false);
  const { hasPermission } = useAuth();

  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStudentEmail, setSelectedStudentEmail] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);

  const [studentOptions, setStudentOptions] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const isFetching = useRef(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  useEffect(() => {
    if (!hasPermission("certificate", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view certificates.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  /* Was `userData.isSuperAdmin` alone, which sent a trainer or a
     coordinator down the student path and asked the API for their own
     certificates. The rule lives in StudentDashboard2 so every page
     agrees on who counts as staff. */
  const checkIfAdmin = (userData) => isStaffViewer(userData);

  const fetchCertificateData = async (email = null) => {
    try {
      // The page shows its own skeleton, so the global overlay stays down.
      silenceFor();
      setLoading(true);
      setError(null);
      const response = await getCertificate(email);
      const { student: studentData, courses: coursesData } = response.data;
      if (studentData) {
        setStudent(studentData);
        setCourses(coursesData || []);
      } else {
        setError(email ? "No certificate data found for this student." : "No certificate data found.");
        setStudent(null);
        setCourses([]);
      }
    } catch (err) {
      setError("Failed to load certificate data. Please try again.");
      setStudent(null);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetching.current = true;
      // Names only (server skips the join): the picker reads name and email.
      const response = await getStudentOptions(20, pageNum, search);
      const students = response.data || [];
      return { students, hasMore: students.length === 20 };
    } catch {
      return { students: [], hasMore: false };
    } finally {
      isFetching.current = false;
    }
  }, []);

  const loadStudents = useCallback(async (search = "", pageNum = 1, append = false) => {
    setLoadingStudents(true);
    const { students, hasMore } = await fetchStudents(search, pageNum);
    const newOptions = students.map((s) => ({ value: s.email, label: `${s.name} | ${s.email}`, student: s }));
    if (append) setStudentOptions((prev) => [...prev, ...newOptions]);
    else setStudentOptions(newOptions);
    setHasMoreStudents(hasMore);
    setLoadingStudents(false);
    return { students, hasMore };
  }, [fetchStudents]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetching.current) return;
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [hasMoreStudents, loadingStudents, studentPage, studentSearchTerm, loadStudents]);

  const handleStudentSearch = useMemo(
    () => debounce(async (inputValue) => {
      setStudentSearchTerm(inputValue);
      setStudentPage(1);
      await loadStudents(inputValue, 1, false);
    }, 700),
    [loadStudents],
  );

  const handleRefresh = useCallback(async () => {
    if (!isAdmin) return;
    setStudentSearchTerm("");
    setStudentPage(1);
    setHasLoadedInitial(false);
    await loadStudents("", 1, false);
    setHasLoadedInitial(true);
  }, [isAdmin, loadStudents]);

  const handleDropdownOpen = useCallback(async () => {
    if (!hasLoadedInitial && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents("", 1, false);
      setHasLoadedInitial(true);
    }
  }, [hasLoadedInitial, studentOptions.length, loadingStudents, loadStudents]);

  const handleStudentSelection = async (selectedOptions) => {
    const selectedOption = selectedOptions?.length > 0 ? selectedOptions[0] : null;
    if (!selectedOption) {
      setSelectedStudentEmail(null);
      setSelectedStudentName("");
      setSelectedStudentValue(null);
      setStudent(null);
      setCourses([]);
      setError(null);
      setStudentSearchTerm("");
      return;
    }
    const { value: email, label: fullLabel } = selectedOption;
    setSelectedStudentEmail(email);
    setSelectedStudentName(fullLabel);
    setSelectedStudentValue(selectedOption);
    await fetchCertificateData(email);
  };

  const handleRetry = async () => {
    if (isAdmin && selectedStudentEmail) await fetchCertificateData(selectedStudentEmail);
    else await fetchCertificateData();
  };

  const fetchStudentDataForCurrentStudent = useCallback(async () => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      silenceFor();
      setLoading(true);
      const currentUser = await getMe();
      const userData = currentUser.data;
      const adminStatus = checkIfAdmin(userData);
      setIsAdmin(adminStatus);
      if (!adminStatus) await fetchCertificateData();
      else setLoading(false);
    } catch {
      setError("Failed to load user data");
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStudentDataForCurrentStudent(); }, [fetchStudentDataForCurrentStudent]);

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

  /* One dropdown, rendered into the gate before a student is picked
     and into the picker strip afterwards. */
  const studentPicker = (
    <CustomAsyncDropdown
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name or email…"
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleDropdownOpen}
      onRefresh={handleRefresh}
    />
  );

  /* ── Staff with nobody selected ── */
  if (isAdmin && !selectedStudentEmail) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="cf-gate">
            <span className="cf-gate-mark">
              <Users size={22} aria-hidden="true" />
            </span>
            <h1 className="cf-gate-title">Pick a student</h1>
            <p className="cf-gate-text">
              Choose a student to see which certificates they have earned.
            </p>
            <div className="cf-gate-field">{studentPicker}</div>
          </div>
        </div>
      </div>
    );
  }

  /* =============================================================
     STAFF VIEW — the shared kit, read as a list
     ============================================================= */
  if (isAdmin) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="cf-picker">
            <div className="cf-picker-id">
              <span className="cf-avatar">{initialsOf(student?.name || selectedStudentName)}</span>
              <div>
                <div className="cf-picker-name">
                  {student?.name || selectedStudentName || "Student"}
                </div>
                <div className="cf-picker-mail">{student?.email || selectedStudentEmail}</div>
              </div>
            </div>

            <div className="cf-picker-field">
              <label className="cf-picker-label">Viewing student</label>
              {studentPicker}
            </div>
          </div>

          <section className="td-tbl td-tbl--cflist">
            <header className="td-tbl-head">
              <div>
                <h5 className="td-tbl-title">Certificates earned</h5>
                <p className="td-tbl-sub">
                  {courses.length} certificate{courses.length === 1 ? "" : "s"} · one per completed
                  course
                </p>
              </div>
            </header>

            {loading ? (
              /* Rows of the table itself, on its three tracks (--cflist). */
              <SkeletonGroup label="Loading certificates" className="td-tbl-rows">
                {[0, 1, 2].map((i) => (
                  <div className="td-tbl-row" key={i}>
                    <div className="td-tbl-name lms-skel-stack">
                      <Skeleton width="70" />
                      <Skeleton width="40" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton width="55" />
                    </div>
                    <div className="td-tbl-name">
                      <Skeleton width="40" />
                    </div>
                  </div>
                ))}
              </SkeletonGroup>
            ) : error ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <AlertCircle size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">Could not load certificates</h6>
                <p className="td-state-text">{error}</p>
                <button type="button" className="cf-retry" onClick={handleRetry}>
                  <RefreshCw size={13} aria-hidden="true" />
                  Try again
                </button>
              </div>
            ) : courses.length === 0 ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <Award size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">No certificates yet</h6>
                <p className="td-state-text">
                  This student has not completed a course that awards one.
                </p>
              </div>
            ) : (
              <>
                {/* Three labels against three tracks in --cflist. */}
                <div className="td-tbl-cols">
                  <span>Certificate</span>
                  <span>Course</span>
                  <span>Issued</span>
                </div>

                <div className="td-tbl-rows">
                  {courses.map((courseObj, idx) => (
                    <div className="td-tbl-row" key={courseObj.id || courseObj.name || idx}>
                      <div className="td-tbl-name">
                        <b>Completion certificate</b>
                        <small>{student?.name}</small>
                      </div>
                      <div className="td-tbl-name">
                        <b>{courseObj.name}</b>
                      </div>
                      <div className="td-tbl-name">
                        <b>{formatDate(courseObj.issuedAt || student?.createdAt) || "—"}</b>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    );
  }

  /* =============================================================
     STUDENT VIEW — the certificates themselves
     ============================================================= */
  return (
    <div className="dashboard-wrapper">
      <div className="cf">
        <div className="cf-wrap">
          <div className="cf-head">
            <div>
              <div className="cf-eyebrow">Academic achievement</div>
              <h1 className="cf-title">My certificates</h1>
            </div>
            {!loading && !error && courses.length > 0 && (
              <span className="cf-count">
                <Award size={14} aria-hidden="true" />
                {courses.length} certificate{courses.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading ? (
            <div className="cf-grid">
              {[0, 1, 2].map((i) => (
                <div className="cf-skel" key={i}>
                  <div className="cf-skel-img" />
                  <div className="cf-skel-body">
                    <div className="cf-skel-bar" />
                    <div className="cf-skel-bar cf-skel-bar--half" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="cf-state cf-state--bad">
              <span className="cf-state-mark">
                <AlertCircle size={24} aria-hidden="true" />
              </span>
              <h2 className="cf-state-title">Unable to load</h2>
              <p className="cf-state-text">{error}</p>
              <button type="button" className="cf-btn cf-btn--ghost" onClick={handleRetry}>
                <RefreshCw size={14} aria-hidden="true" />
                Try again
              </button>
            </div>
          ) : courses.length === 0 ? (
            <div className="cf-state">
              <span className="cf-state-mark">
                <Award size={24} aria-hidden="true" />
              </span>
              <h2 className="cf-state-title">No certificates yet</h2>
              <p className="cf-state-text">
                Finish a course you are enrolled in and its completion certificate will appear
                here.
              </p>
              <button
                type="button"
                className="cf-btn"
                onClick={() => navigate("/suggested-batches")}
              >
                <BookOpen size={14} aria-hidden="true" />
                Browse batches
              </button>
            </div>
          ) : (
            <div className="cf-grid">
              {courses.map((courseObj, idx) => (
                <article className="cf-card" key={courseObj.id || courseObj.name || idx}>
                  <div className="cf-preview">
                    <span className="cf-stamp">
                      <Award size={11} aria-hidden="true" />
                      Certified
                    </span>
                    <img src={certificate_seven_mentor} alt="" />
                    {/* The picture is the house template, the same on every
                        card. Saying so stops it reading as the student's
                        own document. */}
                    <span className="cf-template-note">Template</span>
                  </div>

                  <div className="cf-body">
                    <div className="cf-course">{courseObj.name}</div>
                    <span className="cf-kind">
                      <Award size={11} aria-hidden="true" />
                      Completion certificate
                    </span>

                    <ul className="cf-facts">
                      {student?.name && (
                        <li>
                          <User size={13} aria-hidden="true" />
                          <span className="cf-fact-text">{student.name}</span>
                        </li>
                      )}
                      {student?.email && (
                        <li>
                          <Mail size={13} aria-hidden="true" />
                          <span className="cf-fact-text">{student.email}</span>
                        </li>
                      )}
                      {student?.phone && (
                        <li>
                          <Phone size={13} aria-hidden="true" />
                          <span className="cf-fact-text">{student.phone}</span>
                        </li>
                      )}
                      {formatDate(courseObj.issuedAt || student?.createdAt) && (
                        <li>
                          <Calendar size={13} aria-hidden="true" />
                          <span className="cf-fact-text">
                            Issued {formatDate(courseObj.issuedAt || student?.createdAt)}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div className="cf-foot">
                    <span className="cf-issuer">SevenMentor Pvt. Ltd.</span>
                    <span className="cf-verified">Verified</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Certificate;
